import type { ClientMessage, ServerMessage } from './protocol';

type Handler<K extends ServerMessage['t']> = (msg: Extract<ServerMessage, { t: K }>) => void;

/** A WebSocket to the relay server, with typed messages. */
export class NetClient {
  private ws: WebSocket | null = null;
  private readonly handlers = new Map<string, Set<(msg: ServerMessage) => void>>();
  private readonly closeHandlers = new Set<() => void>();

  constructor(readonly url: string) {}

  get open(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.url);
      this.ws = ws;
      ws.onopen = () => resolve();
      ws.onerror = () => reject(new Error('Serveur injoignable.'));
      ws.onclose = () => {
        if (this.ws === ws) this.ws = null;
        for (const h of this.closeHandlers) h();
      };
      ws.onmessage = (e) => {
        let msg: ServerMessage;
        try { msg = JSON.parse(String(e.data)) as ServerMessage; } catch { return; }
        for (const h of this.handlers.get(msg.t) ?? []) h(msg);
      };
    });
  }

  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  /** Subscribes to one type of message; returns the unsubscribe function. */
  on<K extends ServerMessage['t']>(type: K, handler: Handler<K>): () => void {
    let set = this.handlers.get(type);
    if (!set) this.handlers.set(type, (set = new Set()));
    const h = handler as (msg: ServerMessage) => void;
    set.add(h);
    return () => set!.delete(h);
  }

  onClose(handler: () => void): () => void {
    this.closeHandlers.add(handler);
    return () => this.closeHandlers.delete(handler);
  }

  close(): void {
    const ws = this.ws;
    this.ws = null;
    ws?.close();
  }
}
